// Single dispatch point for opening attachments: resolves an AttachmentTypeEnum from
// explicit type / MIME string / URL extension, then opens the matching shared viewer
// modal with its exact props. Each viewer is dynamic-imported per branch so early-loaded
// consumers (Pinia stores) don't pull all seven viewer chunks into their bundle.

import { EXT_TO_MIME } from "@/constants/fileUpload";
import { AttachmentTypeEnum } from "@/enums/attachment";
import { MimeTypesEnum } from "@/enums/global";
import { openExternalLink } from "@/methods/externalLink";
import { openPdfWithNativeViewer, shouldOpenPdfNatively } from "@/methods/pdfNativeOpener";
import { getAzureUrl, showCustomMessage } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import type {
  AttachmentFallback,
  AttachmentFallbackDecision,
  AttachmentOpenerOptions,
} from "@/types/attachment";
import type { IonModalProps } from "@/types/global";
import type { ComponentRef } from "@ionic/core";

// Lowercased extension incl. the dot (".png"), or "" when none; query-string-safe and
// guards against a trailing-slash path being read as an extension.
export function getAttachmentFileExtension(url: string): string {
  const clean = url.split("?")[0];
  const lastDot = clean.lastIndexOf(".");
  if (lastDot === -1) return "";
  const ext = clean.substring(lastDot);
  return ext.includes("/") ? "" : ext.toLowerCase();
}

// Deliberately no video/audio/presentation mapping: no call site resolves those
// implicitly, and sniffing them would flip .mp4/.mp3 attachments away from the
// new-tab fallback they hit today. Both zip MIME strings are accepted —
// EXT_TO_MIME uses "application/zip" while MimeTypesEnum.ZIP drifted to
// "application/x-zip-compressed".
export function mimeToAttachmentType(mimeType: string): AttachmentTypeEnum | null {
  if (mimeType.startsWith("image/")) return AttachmentTypeEnum.IMAGE;
  if (mimeType === MimeTypesEnum.PDF) return AttachmentTypeEnum.PDF;
  if (mimeType === "application/zip" || mimeType === MimeTypesEnum.ZIP) {
    return AttachmentTypeEnum.ZIP;
  }
  return null;
}

export function resolveAttachmentType(
  url: string,
  mimeType?: string,
  explicitType?: AttachmentTypeEnum,
): AttachmentTypeEnum | null {
  if (explicitType) return explicitType;
  if (mimeType) return mimeToAttachmentType(mimeType);
  const sniffed = mimeToAttachmentType(EXT_TO_MIME[getAttachmentFileExtension(url)] ?? "");
  // A sniffed .zip goes to the fallback (new tab), matching today's call sites —
  // the zip viewer is reachable only via explicit type or MIME.
  return sniffed === AttachmentTypeEnum.ZIP ? null : sniffed;
}

// Whether the simple-PDF path passes the url through verbatim: getAzureUrl corrupts
// data: URIs (offline PDFs), and skipUrlProcessing opts out sites that pass
// raw/base64 content.
export function shouldSkipUrlProcessing(url: string, skipUrlProcessing?: boolean): boolean {
  return Boolean(skipUrlProcessing) || url.startsWith("data:");
}

// Pure decision for an unresolved type; the warn payload prefers the MIME string
// over the url when the caller supplied one.
export function selectAttachmentFallback(
  url: string,
  mimeType?: string,
  fallback: AttachmentFallback = "new-tab",
): AttachmentFallbackDecision {
  if (fallback === "warn") {
    return { action: "warn", payload: mimeType ?? url };
  }
  return { action: "new-tab", url };
}

// Availability probe for a remote attachment before handing it to a viewer that cannot
// detect load failure itself (the heyzine flipbook iframe renders Azure's BlobNotFound
// XML as page content). Returns true ONLY on a definitive non-OK HTTP status. Uses GET,
// not HEAD — the blob storage's CORS rules allow GET but reject HEAD, so a HEAD probe
// failed even for blobs that exist. A rejected fetch (CORS/network) is inconclusive
// ("missing" and "blocked" are indistinguishable), so it fails open: the caller must
// still show the viewer. Consequence: a truly missing blob behind a CORS block still
// renders the provider's error page — that residue cannot be improved client-side.
export async function isAttachmentDefinitelyMissing(url: string): Promise<boolean> {
  try {
    const probe = await fetch(url);
    // The status is all we need — drop the body without downloading the file.
    void probe.body?.cancel().catch(() => undefined);
    return !probe.ok;
  } catch {
    return false;
  }
}

export async function openAttachment(options: AttachmentOpenerOptions): Promise<void> {
  const {
    url,
    name,
    type,
    mimeType,
    pdfVariant = "flip",
    stacking = "inline",
    showDownloadButton,
    skipUrlProcessing,
    forceInAppPdf,
    fallback,
    modalTitle,
    presentationPages,
    extraViewerProps,
    extraModalProps,
  } = options;

  // Resolution and the unresolved-type fallback must run before the first await: on web
  // openExternalLink reaches window.open synchronously, and the pop-up blocker only honours
  // it while the user-gesture call stack is still unbroken.
  const resolvedType = resolveAttachmentType(url, mimeType, type);
  if (!resolvedType) {
    const fallbackDecision = selectAttachmentFallback(url, mimeType, fallback);
    if (fallbackDecision.action === "warn") {
      console.warn("Unsupported file type for viewing:", fallbackDecision.payload);
    } else {
      void openExternalLink(getAzureUrl(fallbackDecision.url));
    }
    return;
  }

  const ionModalStore = useIonModalStore();
  let viewer: ComponentRef;
  let viewerProps: Record<string, unknown>;
  switch (resolvedType) {
    case AttachmentTypeEnum.IMAGE: {
      const { default: ModalImageViewer } =
        await import("@/components/modals/shared/ModalImageViewer.vue");
      viewer = ModalImageViewer;
      viewerProps = { imageUrl: url, imageTitle: name ?? "" };
      break;
    }
    case AttachmentTypeEnum.PDF: {
      if (pdfVariant === "simple") {
        // The ONLY viewer path where the opener applies getAzureUrl — it must run
        // exactly once, and it corrupts data: URIs (offline PDFs), hence the guard
        // and the skipUrlProcessing opt-out for sites that pass raw/base64 content.
        const pdfContent = shouldSkipUrlProcessing(url, skipUrlProcessing) ? url : getAzureUrl(url);
        // On device the OS viewer replaces the in-app canvas entirely: Android WebView has no
        // PDF renderer and iOS only paints page 1 of an iframed PDF. A failed hand-off falls
        // through to the in-app viewer rather than leaving the user with nothing.
        if (
          shouldOpenPdfNatively(useAppSettingsStore().IsNativePlatform, pdfVariant, forceInAppPdf)
        ) {
          if (await openPdfWithNativeViewer(pdfContent, name)) return;
          showCustomMessage({
            messageText: "Couldn't open the PDF in your device viewer. Showing it here instead.",
            messageType: "warning",
          });
        }
        const { default: ModalSimplePdfViewer } =
          await import("@/components/modals/shared/ModalSimplePdfViewer.vue");
        viewer = ModalSimplePdfViewer;
        viewerProps = { pdfContent };
        // Omitted (not passed as undefined) when unset so today's { pdfContent }-only
        // sites stay byte-identical.
        if (name !== undefined) {
          viewerProps.pdfName = name;
        }
      } else {
        const { default: ModalPdfViewer } =
          await import("@/components/modals/shared/ModalPdfViewer.vue");
        viewer = ModalPdfViewer;
        viewerProps = { pdfName: name ?? "", pdfUrl: url };
      }
      if (showDownloadButton !== undefined) {
        viewerProps.showDownloadButton = showDownloadButton;
      }
      break;
    }
    case AttachmentTypeEnum.PRESENTATION: {
      const { default: ModalGoogleDocViewer } =
        await import("@/components/modals/shared/ModalGoogleDocViewer.vue");
      viewer = ModalGoogleDocViewer;
      viewerProps = { docUrl: url };
      break;
    }
    case AttachmentTypeEnum.PRESENTATION_PAGES: {
      const { default: ModalPresentationViewer } =
        await import("@/components/modals/shared/ModalPresentationViewer.vue");
      viewer = ModalPresentationViewer;
      viewerProps = { presentationName: name ?? "", presentationPages: presentationPages ?? [] };
      break;
    }
    case AttachmentTypeEnum.VIDEO: {
      const { default: ModalVideoPlayer } =
        await import("@/components/modals/shared/ModalVideoPlayer.vue");
      viewer = ModalVideoPlayer;
      // Title travels via ModalProps.ModalTitle — the player has no title prop.
      viewerProps = { videoUrl: url };
      break;
    }
    case AttachmentTypeEnum.ZIP: {
      const { default: ModalSimpleZipViewer } =
        await import("@/components/modals/shared/ModalSimpleZipViewer.vue");
      viewer = ModalSimpleZipViewer;
      // RAW url — the viewer applies getAzureUrl internally; applying it here too
      // would double the session param and break the URL.
      viewerProps = { zipFileUrl: url };
      break;
    }
    default:
      return;
  }

  const componentProps = { ...viewerProps, ...extraViewerProps };
  const resolvedModalTitle = modalTitle ?? name;
  // ModalTitle is omitted entirely when both modalTitle and name are unset, so
  // sites whose modalProps carry no title today stay byte-identical.
  const modalProps: Partial<IonModalProps> = {
    ...(resolvedModalTitle !== undefined ? { ModalTitle: resolvedModalTitle } : {}),
    ...extraModalProps,
  };
  if (stacking === "stacked") {
    await ionModalStore.openStackedInlineModal(viewer, componentProps, modalProps);
  } else if (stacking === "default") {
    await ionModalStore.openDefaultModal(viewer, componentProps, modalProps);
  } else {
    await ionModalStore.openInlineModal(viewer, componentProps, modalProps);
  }
}
